import * as React from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Tooltip,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PageLink from "./PageLink";
import "./navbar.scss";
import { useState } from "react";
import { AmplifyUser } from "@aws-amplify/ui";
import { isCoach } from "../../../data/ACL";
import { Link } from "react-router-dom";
import { canViewRebounding } from "../../Pages/Rebounding/access";

interface NavPage {
  label: string;
  route: string;
}

interface NavPageMenu {
  label: string;
  pages: NavPage[];
}

const getPages = (user?: AmplifyUser): (NavPage | NavPageMenu)[] => [
  {
    label: "Reports",
    pages: [
      { label: "NBA", route: "/reports/nba" },
      { label: "G League", route: "/reports/gleague" },
    ],
  },
  // "Explorer" stays the query builder, on its original /explorer/* URLs — saved
  // queries, team-report deep links and research embeds all point at them.
  // "Stats" is the roll-up tables.
  {
    label: "Explorer",
    pages: [
      { label: "NBA", route: "/explorer/nba" },
      { label: "G League / Draft", route: "/explorer/draft" },
    ],
  },
  {
    label: "Stats",
    pages: [
      { label: "NBA", route: "/stats/nba" },
      { label: "G League / Draft", route: "/stats/draft" },
    ],
  },
  // Research wiki — reading is universal, so no role check here.
  // Edit permissions are enforced per-action by ResearchEditor/Admin roles.
  { label: "Research", route: "/research" },
  // 2026-27 player and team KPIs: the dashboards (KD1) and the in-game live sheets.
  {
    label: "KPIs",
    pages: [
      { label: "Season KPIs", route: "/kpi/nba" },
      { label: "Live KPIs", route: "/kpi/nba/live" },
      { label: "Rebounding", route: "/kpi/nba/rebounding" },
    ],
  },
  ...(isCoach(user)
    ? [
        {
          label: "Coaching",
          pages: [{ label: "Rotations", route: "/coaching/rotations" }],
        },
      ]
    : []),
];

const isNavPage = (page: NavPage | NavPageMenu): page is NavPage => {
  return (page as NavPage).route !== undefined;
};

const isNavPageMenu = (page: NavPage | NavPageMenu): page is NavPageMenu => {
  return (page as NavPageMenu).pages !== undefined;
};

interface NavBarProps {
  given_name?: string;
  family_name?: string;
  handleSignOut?: () => void;
  user?: AmplifyUser;
}

export default function NavBar({
  given_name,
  family_name,
  handleSignOut,
  user,
}: NavBarProps) {
  const pages = getPages(user);
  const userInitials = `${given_name ? given_name[0] : ""}${
    family_name ? family_name[0] : ""
  }`;
  // NOTE: Controls to open Navigation Bar menu items.
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const [tiersMenuAnchorEl, setTiersMenuAnchorEl] = useState<{
    el?: HTMLElement | (EventTarget & Element);
    menuIndex?: number;
  }>({});

  const activeMenuPage =
    typeof tiersMenuAnchorEl.menuIndex === "number"
      ? (pages[tiersMenuAnchorEl.menuIndex] as NavPageMenu)
      : undefined;

  return (
    <AppBar position="static" className="header--container">
      {/* Full-bleed, not `maxWidth="xl"`: the pages below run edge to edge, and
          a centred nav bar left the logo and avatar floating inboard of the
          content they belong to. */}
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 } }}>
        <Toolbar disableGutters>
          {/* NOTE: Blazers Desktop Logo */}
          <Box sx={{ display: { xs: "none", md: "flex" }, mr: 1 }}>
            <img
              src="/images/primary-logo.png"
              alt="Primary Team Logo"
              height="50px"
              width="auto"
            />
          </Box>

          <Typography
            variant="h2"
            noWrap
            component="a"
            href="/"
            color="textPrimary"
            sx={{
              mr: 2,
              ml: 2,
              display: { xs: "none", md: "flex" },
              textDecoration: "none",
            }}
          >
            Explorer
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              className="mobile-menu-icon"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: "block", md: "none" },
              }}
            >
              {/* NOTE: Main navigation pages on mobile. */}
              {pages.map((menuPage) => {
                if (isNavPage(menuPage)) {
                  const { label, route } = menuPage;
                  return (
                    <PageLink
                      key={label}
                      label={label}
                      route={route}
                      onClick={handleCloseNavMenu}
                      mobile
                    />
                  );
                } else if (isNavPageMenu(menuPage)) {
                  // Sub-pages are flattened into one list here — there's no
                  // nested menu on mobile — so they carry their parent's label.
                  // Several menus have an "NBA" entry, and without the prefix
                  // they'd be indistinguishable.
                  const { label: menuLabel, pages } = menuPage;
                  return pages.map((subPage, i) => (
                    <PageLink
                      key={`${menuLabel}-${i}`}
                      route={subPage.route}
                      label={`${menuLabel} · ${subPage.label}`}
                      onClick={handleCloseNavMenu}
                      mobile
                    />
                  ));
                }
                return null;
              })}
            </Menu>
          </Box>

          {/* NOTE: Blazers Mobile Logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, mr: 0 }}>
            <img
              src="/images/primary-logo.png"
              alt="Primary Team Logo"
              height="30px"
              width="auto"
            />
          </Box>

          {/* NOTE: Explorer Logo Text for mobile */}
          <Typography
            variant="h3"
            noWrap
            component="a"
            href=""
            color="textPrimary"
            sx={{
              mr: 2,
              ml: 2,
              display: { xs: "flex", md: "none" },
              flexGrow: 1,
              textDecoration: "none",
              textTransform: "uppercase",
            }}
          >
            Explorer
          </Typography>

          {/* NOTE: Main navigation pages displayed on desktop. */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              gap: "20px",
              marginLeft: "10px",
            }}
          >
            {pages.map((menuPage, i) => {
              if (isNavPage(menuPage)) {
                const { label, route } = menuPage;
                return (
                  <PageLink
                    key={i}
                    label={label}
                    route={route}
                    onClick={handleCloseNavMenu}
                  />
                );
              } else if (isNavPageMenu(menuPage)) {
                const { label } = menuPage;
                return (
                  <PageLink
                    key={i}
                    route=""
                    label={label}
                    onClick={(event) => {
                      event.preventDefault();
                      setTiersMenuAnchorEl({
                        el: event.currentTarget,
                        menuIndex: i,
                      });
                    }}
                    menu
                  />
                );
              }
              return null;
            })}
          </Box>

          <Menu
            open={!!tiersMenuAnchorEl.el}
            anchorEl={tiersMenuAnchorEl?.el}
            onClose={() =>
              setTiersMenuAnchorEl((prev) => ({ ...prev, el: undefined }))
            }
          >
            {activeMenuPage?.pages.map((page) => (
              <PageLink
                key={page.label}
                label={page.label}
                route={page.route}
                onClick={() =>
                  setTiersMenuAnchorEl((prev) => ({ ...prev, el: undefined }))
                }
                mobile
              />
            ))}
          </Menu>

          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                {/* NOTE: User Avatar. If no image, falls back to letter with red background. */}
                <Avatar className="header--avatar">{userInitials}</Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {/* NOTE: User menu items. Defined as settings. */}
              {canViewRebounding(user) && (
                <MenuItem component={Link} to="/rebounding" onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">Rebounding board</Typography>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleSignOut && handleSignOut();
                }}
              >
                <Typography textAlign="center">
                  Sign out as {given_name} {family_name}
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
